import { useEffect, useRef, useState, type ReactNode } from 'react';

import { cn } from '~/utils/cn';

type PanelSide = 'left' | 'right';

/** Internal animation phases: closed → entering → open → closing → closed */
type Phase = 'closed' | 'entering' | 'open' | 'closing';

interface SlidingPanelProps {
  /** Controls whether the panel is open. */
  open: boolean;
  /** Which edge the panel slides in from. */
  side: PanelSide;
  children: ReactNode;
  /** Extra classes on the outer grid wrapper (e.g. `shrink-0`). */
  className?: string;
  /** Transition duration in ms. Default: 280. */
  duration?: number;
  /** Automation hook on the outer wrapper (default `sliding-panel`); inner wrapper gets `-content`. */
  testId?: string;
}

/**
 * Wraps a side panel with a smooth slide-in/out transition.
 *
 * Uses `grid-template-columns: 0fr → 1fr` for the width reveal (no hardcoded
 * pixel values) and `translateX(±100%) → 0` for the directional slide. Delays
 * unmounting until the exit transition finishes so no content pops off.
 *
 * State machine: closed → entering → open → closing → closed.
 * All setState calls are deferred to rAF/setTimeout callbacks so React never
 * sees a synchronous setState inside the effect body.
 */
export function SlidingPanel({
  open,
  side,
  children,
  className,
  duration = 280,
  testId = 'sliding-panel',
}: SlidingPanelProps) {
  const [phase, setPhase] = useState<Phase>(() => (open ? 'open' : 'closed'));

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const raf1Ref = useRef<number | null>(null);
  const raf2Ref = useRef<number | null>(null);

  useEffect(() => {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    if (raf1Ref.current !== null) cancelAnimationFrame(raf1Ref.current);
    if (raf2Ref.current !== null) cancelAnimationFrame(raf2Ref.current);

    if (open) {
      // Frame 1: mount with translate offset (entering).
      // Frame 2: remove offset so the CSS transition fires (open).
      raf1Ref.current = requestAnimationFrame(() => {
        setPhase('entering');
        raf2Ref.current = requestAnimationFrame(() => {
          setPhase('open');
        });
      });
    } else {
      // Frame 1: apply exit classes so the CSS transition fires.
      // After duration: unmount.
      raf1Ref.current = requestAnimationFrame(() => {
        setPhase('closing');
        timerRef.current = setTimeout(() => {
          setPhase('closed');
        }, duration);
      });
    }

    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      if (raf1Ref.current !== null) cancelAnimationFrame(raf1Ref.current);
      if (raf2Ref.current !== null) cancelAnimationFrame(raf2Ref.current);
    };
  }, [open, duration]);

  const mounted = phase !== 'closed';
  const visible = phase === 'open';

  if (!mounted) return null;

  const durationCss = `${String(duration)}ms`;
  const easing = 'cubic-bezier(0.4, 0, 0.2, 1)';
  const translateHidden = side === 'right' ? 'translateX(100%)' : 'translateX(-100%)';

  return (
    <div
      aria-hidden={!visible || undefined}
      data-testid={testId}
      className={cn('grid overflow-hidden', className)}
      style={{
        gridTemplateColumns: visible ? '1fr' : '0fr',
        transition: `grid-template-columns ${durationCss} ${easing}`,
      }}
    >
      {/* Inner wrapper clips translated content during the width animation. */}
      <div
        data-testid={`${testId}-content`}
        style={{
          overflow: 'hidden',
          transform: visible ? 'translateX(0)' : translateHidden,
          transition: `transform ${durationCss} ${easing}`,
        }}
      >
        {children}
      </div>
    </div>
  );
}
